// @flow

import * as React from "react"
import {connect} from "react-redux"
import {withTranslation} from "react-i18next"
import {set, cloneDeep} from "lodash"

import {toDinero} from "../util"
import Config from "../config"
import ConfirmModal from "../components/modals/confirm"
import Icon from "../components/icon"
import SendInvoiceModal from "../components/modals/send-invoice"
import Invoiceables from "../components/invoicing/invoiceables"
import BabyListInvoiceItemRow from "../components/invoicing/baby-list-invoice/item-row"
import InvoiceSupplierTotals from "../components/invoicing/supplier-totals"
import InvoiceStatus from "../components/invoicing/status"
import InvoiceHeader from "../components/invoicing/header"
import InvoiceFooter from "../components/invoicing/footer"
import {
  addAllItemsToInvoice,
  addItemToBabyListInvoice,
  finaliseBabyListInvoice,
  payBabyListInvoice,
  setBabyListInvoice,
  setItemsToInvoice,
  setSearchItems,
  setSuppliers,
  sendBabyListInvoice,
  updateBabyListInvoiceMeta,
  updateBabyListInvoiceUseDeposit,
  updateBabyListInvoiceUseGifts,
  deleteBabyListInvoice,
} from "../actions/baby-list-invoice"
import {fetchBabyList, fetchBaby} from "../actions/baby"
import {hasInvoiceableWebshopSource} from "../reducers/baby"
import {groupItemsToInvoice} from "../reducers/baby-list-invoice"
import type {
  Dispatch,
  BabyListInvoice,
  SearchItem,
  ItemToInvoice,
  InvoiceBase,
  State as ReduxState,
} from "../types"

type Props = {
  +t: any,
  +babyToken: string,
  +lang: string,
  +babyListInvoice: ?BabyListInvoice,
  +itemsToInvoice: $ReadOnlyArray<ItemToInvoice>,
  +searchItems: $ReadOnlyArray<SearchItem>,
  +suppliers: $ReadOnlyArray<Supplier>,
  +match: any,
  +dispatch: Dispatch,
  +finaliseError: boolean,
}

type State = {
  +isConfirmFinaliseModalOpen: boolean,
  +isConfirmDeleteModalOpen: boolean,
  +isConfirmPayModalOpen: boolean,
  +isSendModalOpen: boolean,
  +note: string,
  +isFinal: ?boolean,
}

class BabyListInvoiceView extends React.Component<Props, State> {
  handleAddInvoiceable: (ItemToInvoice) => void
  handleFinaliseClick: () => void
  handleDeleteClick: () => void
  handlePayClick: () => void
  handlePreviewClick: () => void
  handleConfirmFinalise: () => void
  handleConfirmDelete: () => void
  handleCancelFinalise: () => void
  handleCancelDelete: () => void
  handleConfirmPay: () => void
  handleCancelPay: () => void
  handleCloseModal: () => void
  handleSettleChange: (SyntheticInputEvent<HTMLInputElement>) => void
  handleAmountDiscountUpdate: (string, string) => void
  handleNoteChange: (string) => void
  handleSend: (string) => void
  handleInvoiceHeaderUpdate: (InvoiceBase) => void
  handleAddAllItemsToInvoiceClick: (
    SyntheticMouseEvent<HTMLLinkElement>
  ) => void

  constructor(props: Props) {
    super(props)

    this.state = {
      isConfirmFinaliseModalOpen: false,
      isConfirmDeleteModalOpen: false,
      isConfirmPayModalOpen: false,
      isSendModalOpen: false,
      note: "",
      isFinal: props.babyListInvoice
        ? props.babyListInvoice.isFinal
        : undefined,
    }

    props.i18n.loadLanguages([props.lang])

    this.handleAmountDiscountUpdate = this.handleAmountDiscountUpdate.bind(this)
    this.handleAddInvoiceable = this.handleAddInvoiceable.bind(this)
    this.handleFinaliseClick = this.handleFinaliseClick.bind(this)
    this.handleDeleteClick = this.handleDeleteClick.bind(this)
    this.handlePayClick = this.handlePayClick.bind(this)
    this.handlePreviewClick = this.handlePreviewClick.bind(this)
    this.handleConfirmFinalise = this.handleConfirmFinalise.bind(this)
    this.handleConfirmDelete = this.handleConfirmDelete.bind(this)
    this.handleCancelFinalise = this.handleCancelFinalise.bind(this)
    this.handleCancelDelete = this.handleCancelDelete.bind(this)
    this.handleConfirmPay = this.handleConfirmPay.bind(this)
    this.handleCancelPay = this.handleCancelPay.bind(this)
    this.handleCloseModal = this.handleCloseModal.bind(this)
    this.handleInvoiceHeaderUpdate = this.handleInvoiceHeaderUpdate.bind(this)
    this.handleAddAllItemsToInvoiceClick =
      this.handleAddAllItemsToInvoiceClick.bind(this)
    this.handleSettleChange = this.handleSettleChange.bind(this)
    this.handleNoteChange = this.handleNoteChange.bind(this)
    this.handleSend = this.handleSend.bind(this)
  }

  componentDidMount() {
    const {dispatch} = this.props
    if (Config.data.babyListInvoice) {
      const {babyListInvoice, babyToken} = Config.data
      dispatch(fetchBaby(babyToken))
      dispatch(fetchBabyList(babyToken))
      // for dashboard views, the base entities are already output in the html
      dispatch(setBabyListInvoice(babyListInvoice.babyListInvoice))
      dispatch(
        setItemsToInvoice(groupItemsToInvoice(babyListInvoice.itemsToInvoice))
      )
      dispatch(setSearchItems(babyListInvoice.searchItems))
      dispatch(setSuppliers(babyListInvoice.suppliers))
    }
  }

  static getDerivedStateFromProps({babyListInvoice}, state) {
    if (babyListInvoice && babyListInvoice.note && state.note == "") {
      return {note: babyListInvoice.note}
    }
    if (babyListInvoice && babyListInvoice.isFinal && state.isFinal == false) {
      return {
        isFinal: babyListInvoice.isFinal,
        isConfirmModalOpen: false,
        isSendModalOpen: true,
      }
    }
    if (babyListInvoice) {
      return {isFinal: babyListInvoice.isFinal}
    }
    return null
  }

  handleAddInvoiceable(babyListItem) {
    const {dispatch, babyListInvoice, babyToken} = this.props
    if (babyListInvoice) {
      dispatch(
        addItemToBabyListInvoice(babyToken, babyListInvoice.id, babyListItem.id)
      )
    }
  }

  handleAmountDiscountUpdate(property: string, value: string) {
    const {dispatch, babyListInvoice, babyToken} = this.props
    if (babyListInvoice) {
      const babyListInvoiceUpdated = cloneDeep(babyListInvoice)
      set(babyListInvoiceUpdated, property, value)
      dispatch(
        updateBabyListInvoiceMeta(
          babyToken,
          babyListInvoice.id,
          babyListInvoiceUpdated
        )
      )
    }
  }

  handleFinaliseClick() {
    const {babyListInvoice} = this.props
    if (!babyListInvoice) return null

    if (babyListInvoice.isFinal) {
      this.setState({
        isConfirmFinaliseModalOpen: false,
        isConfirmPayModalOpen: false,
        isSendModalOpen: true,
      })
    } else {
      this.setState({isConfirmFinaliseModalOpen: true})
    }
  }

  handleDeleteClick() {
    const {babyListInvoice} = this.props
    if (!babyListInvoice) return null

    if (babyListInvoice.isFinal) {
      this.setState({
        isConfirmFinaliseModalOpen: false,
        isConfirmDeleteModalOpen: false,
        isConfirmPayModalOpen: false,
        isSendModalOpen: false,
      })
    } else {
      this.setState({isConfirmDeleteModalOpen: true})
    }
  }

  handlePayClick() {
    const {babyListInvoice} = this.props
    if (!babyListInvoice) return null

    if (!babyListInvoice.isPaid && babyListInvoice.isFinal) {
      this.setState({
        isConfirmFinaliseModalOpen: false,
        isConfirmPayModalOpen: true,
      })
    }
  }

  handlePreviewClick() {
    this.setState({isSendModalOpen: true})
  }

  handleConfirmFinalise() {
    const {dispatch, babyListInvoice, babyToken} = this.props
    if (babyListInvoice) {
      dispatch(finaliseBabyListInvoice(babyToken, babyListInvoice.id))
      this.setState({isConfirmFinaliseModalOpen: false})
    }
  }

  handleCancelFinalise() {
    this.setState({isConfirmFinaliseModalOpen: false})
  }

  handleConfirmDelete() {
    const {dispatch, babyListInvoice, babyToken} = this.props
    if (babyListInvoice) {
      dispatch(deleteBabyListInvoice(babyToken, babyListInvoice.id))
      this.setState({isConfirmDeleteModalOpen: false})
    }
  }

  handleCancelDelete() {
    this.setState({isConfirmDeleteModalOpen: false})
  }

  handleConfirmPay() {
    const {dispatch, babyListInvoice, babyToken} = this.props
    if (babyListInvoice) {
      dispatch(payBabyListInvoice(babyToken, babyListInvoice.id))
      this.setState({isConfirmPayModalOpen: false})
    }
  }

  handleCancelPay() {
    this.setState({isConfirmPayModalOpen: false})
  }

  handleCloseModal() {
    this.setState({
      isConfirmPayModalOpen: false,
      isConfirmFinaliseModalOpen: false,
      isSendModalOpen: false,
    })
  }

  handleAddAllItemsToInvoiceClick(e: SyntheticMouseEvent<HTMLLinkElement>) {
    e.preventDefault()
    const {dispatch, babyListInvoice, babyToken} = this.props
    if (babyListInvoice) {
      dispatch(addAllItemsToInvoice(babyToken, babyListInvoice.id))
    }
  }

  handleSettleChange(e: SyntheticInputEvent<HTMLInputElement>) {
    const {dispatch, babyListInvoice, babyToken} = this.props
    if (babyListInvoice) {
      const fn =
        e.currentTarget.name == "settleGifts"
          ? updateBabyListInvoiceUseGifts
          : updateBabyListInvoiceUseDeposit
      dispatch(fn(babyToken, babyListInvoice.id, e.currentTarget.checked))
    }
  }

  handleNoteChange(note: string) {
    const {babyToken, babyListInvoice, dispatch} = this.props
    if (babyListInvoice) {
      dispatch(updateBabyListInvoiceMeta(babyToken, babyListInvoice.id, {note}))
    }
  }

  handleInvoiceHeaderUpdate(invoice: InvoiceBase) {
    const {babyToken, dispatch} = this.props
    dispatch(updateBabyListInvoiceMeta(babyToken, invoice.id, invoice))
  }

  handleSend(message: string) {
    const {babyToken, babyListInvoice, dispatch} = this.props
    if (babyListInvoice) {
      dispatch(sendBabyListInvoice(babyToken, babyListInvoice.id, message))
    }
    this.handleCloseModal()
  }

  renderModal() {
    const {
      isConfirmFinaliseModalOpen,
      isConfirmDeleteModalOpen,
      isConfirmPayModalOpen,
      isSendModalOpen,
    } = this.state
    const {babyListInvoice, user, t, lang} = this.props
    if (!babyListInvoice) return
    if (isConfirmFinaliseModalOpen) {
      return (
        <ConfirmModal
          onConfirm={this.handleConfirmFinalise}
          onCancel={this.handleCancelFinalise}
        />
      )
    }
    if (isConfirmDeleteModalOpen) {
      return (
        <ConfirmModal
          onConfirm={this.handleConfirmDelete}
          onCancel={this.handleCancelDelete}
        />
      )
    }
    if (isConfirmPayModalOpen) {
      return (
        <ConfirmModal
          onConfirm={this.handleConfirmPay}
          onCancel={this.handleCancelPay}
        />
      )
    }
    if (isSendModalOpen) {
      const title = babyListInvoice.isFinal
        ? t("intake.sendBabyListInvoice.title", {lng: lang})
        : t("intake.sendBabyListInvoice.title_preview", {lng: lang})
      const message = babyListInvoice.isFinal
        ? t("intake.sendBabyListInvoice.defaultFinalMessage", {lng: lang})
        : t("intake.sendBabyListInvoice.defaultPreviewMessage", {
            baby_name: "",
            google_business_url:
              user.googleBusinessURL || "'url van je google my business'",
            lng: lang,
          })
      const text = t(`intake.sendBabyListInvoice.info`, {lng: lang})
      return (
        <SendInvoiceModal
          title={title}
          defaultMessage={message}
          text={text}
          email={babyListInvoice.receiver.email}
          onSend={this.handleSend}
          onCloseModal={this.handleCloseModal}
        />
      )
    }
  }

  renderActionButtons() {
    const {t, babyListInvoice} = this.props
    if (!babyListInvoice) return null
    const {isFinal, isPaid} = babyListInvoice

    return (
      <>
        {!isFinal && (
          <button
            className="btn btn-secondary mr-2"
            type="button"
            onClick={this.handlePreviewClick}
          >
            {t("invoice.sendDraft")}
          </button>
        )}
        <button
          className="btn btn-primary mr-2"
          type="button"
          onClick={this.handleFinaliseClick}
        >
          {isFinal ? t("invoice.finalise_send") : t("invoice.finalise")}
        </button>
        {isFinal ? (
          <button
            className="btn btn-secondary mr-2"
            type="button"
            disabled={isPaid}
            onClick={this.handlePayClick}
          >
            {isPaid ? t("invoice.paid") : t("invoice.pay")}
          </button>
        ) : null}
        {!isFinal && (
          <button
            className="btn btn-danger mr-2"
            type="button"
            onClick={this.handleDeleteClick}
          >
            {t("invoice.deleteDraft")}
          </button>
        )}
        <a
          href={Config.data.babyListInvoice.pdfUrl}
          className="d-inline-flex align-items-center btn"
          target="blank"
        >
          <Icon name="icon-pdf" />
          <span className="d-inline-block pl-1">{t("invoice.viewPdf")}</span>
        </a>
        <a
          href={Config.data.babyListInvoice.csvUrl}
          className="d-inline-flex align-items-center btn"
          target="blank"
        >
          <Icon name="icon-xlsx" />
          <span className="d-inline-block pl-1">{t("invoice.viewCsv")}</span>
        </a>
      </>
    )
  }

  renderSide() {
    const {
      t,
      babyListInvoice,
      itemsToInvoice,
      searchItems,
      suppliers,
      babyList,
      baby,
    } = this.props
    if (!babyListInvoice) return null
    const {
      isFinal,
      hasGift,
      hasDeposit,
      hasDepositLeft,
      hasGiftLeft,
      amountDepositLeft,
      amountGiftLeft,
    } = babyListInvoice
    const showDeposit = hasDeposit || hasDepositLeft
    const showGift = hasGift || hasGiftLeft
    const amountGiftLeftNoticeLabel =
      "(" + toDinero(amountGiftLeft).toFormat("$0,0.00") + ")"
    let amountGiftLeftNotice = null
    if (!hasGift) {
      amountGiftLeftNotice = (
        <React.Fragment> {amountGiftLeftNoticeLabel}</React.Fragment>
      )
    } else if (hasGiftLeft) {
      amountGiftLeftNotice = (
        <React.Fragment>
          {" "}
          <span className="text-danger leading-none block amount-gift-notice-label">
            {amountGiftLeftNoticeLabel}
          </span>
        </React.Fragment>
      )
    }
    const intakeNotInvoiceItems = searchItems.filter((i) => !i.canInvoice)
    return (
      <div className="invoice-side">
        {!isFinal && itemsToInvoice.length > 0 ? (
          <>
            {this.renderActionButtons()}
            <div className="invoice-side-block panel mt-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h2 className="m-0">{t("invoice.itemsToInvoice")}</h2>
                <a
                  href="#"
                  className="btn btn-sm btn-outline-success"
                  onClick={this.handleAddAllItemsToInvoiceClick}
                >
                  {t("invoice.addAllItemsToInvoice")}
                </a>
              </div>
              <Invoiceables
                items={itemsToInvoice}
                onItemClick={this.handleAddInvoiceable}
              />
              <a
                href="#"
                className="btn btn-sm btn-outline-success mt-4"
                onClick={this.handleAddAllItemsToInvoiceClick}
              >
                {t("invoice.addAllItemsToInvoice")}
              </a>
            </div>
          </>
        ) : null}

        {!isFinal && intakeNotInvoiceItems.length > 0 ? (
          <>
            <div className="invoice-side-block panel mt-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h2 className="m-0">{t("invoice.itemsIntakeNotInvoice")}</h2>
              </div>
              <Invoiceables items={intakeNotInvoiceItems} />
            </div>
          </>
        ) : null}

        {babyList &&
        baby?.states.includes("signed") &&
        hasInvoiceableWebshopSource(babyList, "intake_not_invoiced") &&
        hasInvoiceableWebshopSource(babyList, "bundling") ? (
          <div className="invoice-side-block panel bg-warning">
            We zien dat er een gebundeld product op de lijst staat met nog te
            factureren webshop producten van het type{" "}
            <strong>&quot;intake_not_invoiced&quot;</strong>.
            <br />
            <a
              href={`/intake/${baby.token}/list`}
              className="btn mt-4"
              style={{color: "#fff", backgroundColor: "#000"}}
            >
              Bekijk hier
            </a>
          </div>
        ) : null}

        {(showDeposit || showGift) && (
          <div className="invoice-side-block panel">
            <h2>{t("invoice.settings")}</h2>
            {showGift && (
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="settleGifts"
                  id="settleGifts"
                  disabled={isFinal}
                  checked={hasGift}
                  onChange={this.handleSettleChange}
                />
                <label className="form-check-label " htmlFor="settleGifts">
                  {t("invoice.settleGifts")}
                  {amountGiftLeftNotice}
                </label>
              </div>
            )}
            {showDeposit && (
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="settleDeposit"
                  id="settleDeposit"
                  disabled={isFinal}
                  checked={hasDeposit}
                  onChange={this.handleSettleChange}
                />
                <label className="form-check-label" htmlFor="settleDeposit">
                  {t("invoice.settleDeposit")} (
                  {toDinero(amountDepositLeft).toFormat("$0,0.00")})
                  {Config.data.babyListInvoice.depositEditUrl.length > 0 &&
                  !toDinero(amountDepositLeft).isZero() ? (
                    <a
                      href={Config.data.babyListInvoice.depositEditUrl}
                      className="ml-2"
                    >
                      edit
                    </a>
                  ) : null}
                </label>
              </div>
            )}
          </div>
        )}

        <InvoiceSupplierTotals
          items={babyListInvoice.itemsGrouped}
          searchItems={searchItems}
          suppliers={suppliers}
        />

        {this.renderActionButtons()}
      </div>
    )
  }

  render() {
    const {t, babyListInvoice} = this.props
    if (!babyListInvoice) return null
    const {isFinal, isEditable, itemsGrouped} = babyListInvoice
    return (
      <div className="container invoice mt-4">
        <div className="invoice-sheet">
          <InvoiceStatus invoice={babyListInvoice} />

          <InvoiceHeader
            invoice={babyListInvoice}
            finaliseError={this.props.finaliseError}
            onUpdate={this.handleInvoiceHeaderUpdate}
          />

          <div className="invoice-sheet-content">
            <table className="table table-sm">
              <colgroup>
                <col width="" />
                <col width="80" />
                <col width="80" />
                <col width="70" />
                <col width="70" />
                <col width="85" />
              </colgroup>

              <thead>
                <tr>
                  <th>{t("invoice.product")}</th>
                  <th className="text-center">{t("invoice.amount")}</th>
                  <th className="text-right">{t("invoice.discount")}</th>
                  <th className="text-right">{t("invoice.price")}</th>
                  <th className="text-right">{t("invoice.price_total")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {itemsGrouped.map(({categoryName, categoryId, items}) => (
                  <React.Fragment key={`invoice-cat-${categoryId}`}>
                    <tr>
                      <td colSpan="6" className="p-0">
                        &nbsp;
                      </td>
                    </tr>
                    <tr className="thead-light">
                      <th colSpan="6">
                        <h3 className="mb-0">{categoryName}</h3>
                      </th>
                    </tr>
                    {items.map((i) => (
                      <BabyListInvoiceItemRow
                        item={i}
                        isFinal={isFinal}
                        isEditable={isEditable}
                        key={"invoice-item-" + i.id}
                      />
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <InvoiceFooter
            isInclVAT={true}
            invoice={babyListInvoice}
            onNoteChange={this.handleNoteChange}
            onAmountDiscountUpdate={this.handleAmountDiscountUpdate}
          />
        </div>

        {this.renderSide()}
        {this.renderModal()}
      </div>
    )
  }
}

const mapStateToProps = (state: ReduxState) => ({
  babyToken: Config.data.babyToken,
  lang: Config.data.lang,
  baby: state.baby.baby,
  babyListInvoice: state.babyListInvoice.babyListInvoice,
  finaliseError: state.babyListInvoice.finaliseError,
  itemsToInvoice: state.babyListInvoice.itemsToInvoice,
  searchItems: state.babyListInvoice.searchItems,
  suppliers: state.babyListInvoice.suppliers,
  user: state.user.entity,
  babyList: state.baby.list,
})

export default withTranslation()(connect(mapStateToProps)(BabyListInvoiceView))
